"use client";
import React, { useRef, useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface MerchantItem {
  name: string;
  logo?: string;
  href: string;
}

interface Props {
  merchants: MerchantItem[];
}

const CARD_STEP = 150;        // 130px card + 20px gap
const DURATION = 40;          // seconds for full loop (same as original)
const AUTO_MS = 2500;         // auto-scroll interval when hovering is off

export default function RoundedMerchantCarousel({ merchants }: Props) {
  const trackRef   = useRef<HTMLDivElement>(null);
  const autoRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const offsetRef  = useRef(0);          // current pixel offset we've applied
  const [hovered, setHovered] = useState(false);

  // tripled list for seamless loop
  const tripled = [...merchants, ...merchants, ...merchants];

  // Total width of ONE copy of the list
  const oneWidth = merchants.length * CARD_STEP;

  /* apply offset to the track via CSS translate so it stacks on top of the marquee animation */
  const applyOffset = (px: number) => {
    // keep offset within one full copy so the loop stays seamless
    const clamped = ((px % oneWidth) + oneWidth) % oneWidth;
    offsetRef.current = clamped;
    const el = trackRef.current;
    if (el) el.style.setProperty("--offset", `-${clamped}px`);
  };

  const scrollOneCard = (dir: "prev" | "next") => {
    const delta = dir === "next" ? CARD_STEP : -CARD_STEP;
    applyOffset(offsetRef.current + delta);
  };

  /* auto-scroll: advance one card every AUTO_MS when not hovered */
  useEffect(() => {
    if (hovered) {
      if (autoRef.current) clearInterval(autoRef.current);
      return;
    }
    autoRef.current = setInterval(() => {
      applyOffset(offsetRef.current + CARD_STEP);
    }, AUTO_MS);
    return () => { if (autoRef.current) clearInterval(autoRef.current); };
  }, [hovered]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes ticker {
          from { transform: translateX(var(--offset, 0px)); }
          to   { transform: translateX(calc(var(--offset, 0px) - ${oneWidth}px)); }
        }
        .ticker-track {
          display: flex;
          width: max-content;
          --offset: 0px;
          animation: ticker ${DURATION}s linear infinite;
          will-change: transform;
        }
        .ticker-track.paused {
          animation-play-state: paused;
        }
      ` }} />

      <div
        className="relative"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* fade masks */}
        <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-zinc-50 to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-zinc-50 to-transparent z-10 pointer-events-none" />

        {/* Prev button */}
        <button
          onClick={() => scrollOneCard("prev")}
          aria-label="Previous stores"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20
            w-9 h-9 rounded-full flex items-center justify-center
            bg-white border border-slate-200 shadow-md text-slate-600
            hover:bg-indigo-950 hover:border-indigo-950 hover:text-white
            transition-all duration-200 cursor-pointer"
        >
          <ChevronLeft size={18} strokeWidth={2.5} />
        </button>

        {/* Next button */}
        <button
          onClick={() => scrollOneCard("next")}
          aria-label="Next stores"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20
            w-9 h-9 rounded-full flex items-center justify-center
            bg-white border border-slate-200 shadow-md text-slate-600
            hover:bg-indigo-950 hover:border-indigo-950 hover:text-white
            transition-all duration-200 cursor-pointer"
        >
          <ChevronRight size={18} strokeWidth={2.5} />
        </button>

        {/* ticker strip */}
        <div className="overflow-hidden py-3">
          <div
            ref={trackRef}
            className={`ticker-track gap-5${hovered ? " paused" : ""}`}
          >
            {tripled.map((merchant, i) => (
              <Link
                key={i}
                href={merchant.href}
                className="no-underline group/card flex-shrink-0 flex flex-col items-center gap-3 w-[130px]"
              >
                <div className="relative w-[110px] h-[110px] bg-white border-2 border-slate-100
                  rounded-[1.5rem] flex items-center justify-center p-4 overflow-hidden
                  shadow-[0_2px_12px_rgba(0,0,0,0.04)]
                  group-hover/card:border-indigo-800/20
                  group-hover/card:shadow-[0_10px_32px_rgba(55,48,163,0.10)]
                  group-hover/card:-translate-y-1.5
                  transition-all duration-300">

                  {merchant.logo ? (
                    <Image
                      src={merchant.logo}
                      alt={merchant.name}
                      fill
                      className="object-contain p-3 group-hover/card:scale-110 transition-transform duration-300"
                      unoptimized
                    />
                  ) : (
                    <span className="text-indigo-800 font-black text-xl uppercase">
                      {merchant.name?.[0]}
                    </span>
                  )}

                  <span className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-emerald-500
                    opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 shadow-sm" />
                </div>

                <span className="text-[11px] font-semibold text-slate-500
                  group-hover/card:text-slate-900 transition-colors duration-200
                  text-center line-clamp-1 w-full px-1">
                  {merchant.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
